/**
 * MONSTER TOWN — 2D PIECE ICONS. SHARED ART (src/render/**), additive.
 *
 * Moved here from src/build/icons.ts, unchanged, because TWO crews need the
 * same picture of the same piece: the build crew's piece drawer and the HUD
 * crew's shop panel. Crews never import each other, so the drawing table lives
 * in the shared art layer and both import it from here. src/build/icons.ts
 * re-exports these three functions, so its public surface is exactly as it was.
 *
 * LAW #4: no reading required. Every tile is a picture, generated in code
 * (zero external assets). Icons are keyed by CATEGORY and coloured from the
 * def's own tint list, so a crew that drops in a new furniture set or venue
 * fixture gets a sensible icon for free. A def may override with `icon2d`.
 */
import { css, type PaletteName } from '../core/palette'
import type { AnyPlaceableDef, PlaceableCategory } from '../core/placeable'

const INK = css('ink')

/** Everything is drawn in a 100x100 box and scaled to fit the tile. */
type Draw = (c: CanvasRenderingContext2D, color: string, alt: string) => void

const DEFAULT_COLOR: Record<PlaceableCategory, PaletteName> = {
  block: 'woodLight',
  floor: 'floorWood',
  wall: 'wallCream',
  roof: 'roofRed',
  door: 'doorWood',
  window: 'glass',
  stair: 'wood',
  path: 'path',
  fence: 'woodLight',
  furniture: 'fabric',
  bed: 'fabricAlt',
  kitchen: 'metal',
  bath: 'wallBlue',
  light: 'uiCoin',
  decor: 'flowerPurple',
  plant: 'flowerPink',
  tree: 'leaf',
  water: 'water',
  townProp: 'roofTeal',
  venueFixture: 'iceStrawberry',
  toy: 'flowerRed',
  appliance: 'metalDark',
}

function fill(c: CanvasRenderingContext2D, color: string): void {
  c.fillStyle = color
  c.fill()
  c.lineWidth = 5
  c.lineJoin = 'round'
  c.strokeStyle = INK
  c.stroke()
}

function rect(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number, color: string): void {
  c.beginPath()
  const rr = Math.min(r, w / 2, h / 2)
  c.moveTo(x + rr, y)
  c.arcTo(x + w, y, x + w, y + h, rr)
  c.arcTo(x + w, y + h, x, y + h, rr)
  c.arcTo(x, y + h, x, y, rr)
  c.arcTo(x, y, x + w, y, rr)
  c.closePath()
  fill(c, color)
}

function poly(c: CanvasRenderingContext2D, pts: readonly [number, number][], color: string): void {
  c.beginPath()
  const first = pts[0]
  if (first === undefined) return
  c.moveTo(first[0], first[1])
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i]
    if (p !== undefined) c.lineTo(p[0], p[1])
  }
  c.closePath()
  fill(c, color)
}

function circle(c: CanvasRenderingContext2D, x: number, y: number, r: number, color: string): void {
  c.beginPath()
  c.arc(x, y, r, 0, Math.PI * 2)
  fill(c, color)
}

// ---------------------------------------------------------------------------
// the drawings
// ---------------------------------------------------------------------------

const cube: Draw = (c, color, alt) => {
  poly(c, [[50, 12], [88, 33], [50, 54], [12, 33]], alt)
  poly(c, [[12, 33], [50, 54], [50, 90], [12, 69]], color)
  poly(c, [[88, 33], [88, 69], [50, 90], [50, 54]], alt)
}

const wallIcon: Draw = (c, color, alt) => {
  rect(c, 14, 22, 72, 60, 6, color)
  c.strokeStyle = alt
  c.lineWidth = 4
  for (const y of [42, 62]) {
    c.beginPath()
    c.moveTo(18, y)
    c.lineTo(82, y)
    c.stroke()
  }
  c.beginPath()
  c.moveTo(50, 22)
  c.lineTo(50, 42)
  c.moveTo(34, 42)
  c.lineTo(34, 62)
  c.moveTo(66, 42)
  c.lineTo(66, 62)
  c.stroke()
}

const roofIcon: Draw = (c, color, alt) => {
  poly(c, [[50, 14], [92, 56], [8, 56]], color)
  rect(c, 12, 56, 76, 12, 4, alt)
}

const floorIcon: Draw = (c, color, alt) => {
  rect(c, 12, 26, 76, 52, 6, color)
  c.fillStyle = alt
  c.fillRect(12, 26, 38, 26)
  c.fillRect(50, 52, 38, 26)
  rect(c, 12, 26, 76, 52, 6, 'transparent')
}

const pathIcon: Draw = (c, color) => {
  poly(c, [[26, 30], [58, 22], [64, 40], [30, 48]], color)
  poly(c, [[34, 56], [70, 50], [74, 70], [38, 76]], color)
}

const doorIcon: Draw = (c, color, alt) => {
  rect(c, 22, 14, 56, 74, 8, alt)
  rect(c, 32, 24, 36, 64, 6, color)
  circle(c, 60, 56, 5, INK)
}

const windowIcon: Draw = (c, color, alt) => {
  rect(c, 18, 22, 64, 58, 8, alt)
  rect(c, 26, 30, 48, 42, 4, color)
  c.strokeStyle = INK
  c.lineWidth = 5
  c.beginPath()
  c.moveTo(50, 30)
  c.lineTo(50, 72)
  c.moveTo(26, 51)
  c.lineTo(74, 51)
  c.stroke()
}

const stairIcon: Draw = (c, color) => {
  poly(c, [[16, 84], [16, 62], [40, 62], [40, 42], [64, 42], [64, 22], [88, 22], [88, 84]], color)
}

const fenceIcon: Draw = (c, color, alt) => {
  rect(c, 10, 40, 80, 9, 3, alt)
  rect(c, 10, 60, 80, 9, 3, alt)
  for (const x of [20, 46, 72]) poly(c, [[x, 26], [x + 14, 26], [x + 14, 84], [x, 84]], color)
}

const chairIcon: Draw = (c, color, alt) => {
  rect(c, 26, 18, 48, 40, 8, color)
  rect(c, 20, 52, 60, 16, 6, alt)
  rect(c, 26, 66, 9, 20, 3, alt)
  rect(c, 65, 66, 9, 20, 3, alt)
}

const bedIcon: Draw = (c, color, alt) => {
  rect(c, 12, 44, 76, 30, 8, color)
  rect(c, 18, 34, 30, 18, 6, alt)
}

const lampIcon: Draw = (c, color, alt) => {
  poly(c, [[32, 20], [68, 20], [78, 52], [22, 52]], color)
  rect(c, 45, 52, 10, 26, 3, alt)
  rect(c, 32, 76, 36, 10, 4, alt)
}

const plantIcon: Draw = (c, color, alt) => {
  rect(c, 46, 46, 8, 40, 3, alt)
  circle(c, 50, 34, 18, color)
  circle(c, 50, 34, 6, css('flowerYellow'))
}

const treeIcon: Draw = (c, color, alt) => {
  rect(c, 43, 52, 14, 34, 4, alt)
  circle(c, 50, 36, 26, color)
}

const waterIcon: Draw = (c, color, alt) => {
  circle(c, 50, 52, 32, color)
  c.strokeStyle = alt
  c.lineWidth = 6
  c.beginPath()
  c.moveTo(30, 48)
  c.quadraticCurveTo(40, 40, 50, 48)
  c.quadraticCurveTo(60, 56, 70, 48)
  c.stroke()
}

const propIcon: Draw = (c, color, alt) => {
  rect(c, 40, 34, 20, 52, 6, alt)
  rect(c, 24, 20, 52, 20, 8, color)
}

const foodIcon: Draw = (c, color, alt) => {
  circle(c, 50, 34, 22, color)
  poly(c, [[32, 46], [68, 46], [50, 88]], alt)
}

const starIcon: Draw = (c, color) => {
  const pts: [number, number][] = []
  for (let i = 0; i < 10; i++) {
    const a = (Math.PI / 5) * i - Math.PI / 2
    const r = i % 2 === 0 ? 38 : 17
    pts.push([50 + Math.cos(a) * r, 52 + Math.sin(a) * r])
  }
  poly(c, pts, color)
}

// ---------------------------------------------------------------------------
// PER-PIECE PICTURES — "the picture IS the label" (LAW #4)
// ---------------------------------------------------------------------------
//
// Category alone is not a label. Every `venueFixture` used to fall through to
// one pink-scoop-on-a-cone drawing, so five ice cream tubs, three cooking pots,
// two base stacks and a topping jar were TEN IDENTICAL TILES sitting next to
// each other — two of them wearing different price badges, which is the only
// way a kid could have told them apart, and a kid who cannot read cannot.
//
// So a piece may claim its own drawing by id. This is a lookup table, not
// engine code: a new venue or furniture set adds rows here (or ships its own
// `icon2d`) and nothing else changes.

const tubIcon: Draw = (c, color, alt) => {
  poly(c, [[24, 46], [76, 46], [70, 88], [30, 88]], alt)
  circle(c, 50, 40, 20, color)
  circle(c, 36, 34, 9, color)
  circle(c, 64, 34, 9, color)
}

const jarIcon: Draw = (c, color, alt) => {
  rect(c, 30, 34, 40, 54, 10, color)
  rect(c, 26, 18, 48, 18, 6, alt)
  for (const [x, y] of [[40, 52], [56, 60], [46, 70], [60, 44]] as [number, number][]) {
    circle(c, x, y, 5, alt)
  }
}

const coneStackIcon: Draw = (c, color, alt) => {
  poly(c, [[34, 20], [66, 20], [50, 60]], color)
  poly(c, [[28, 40], [72, 40], [50, 92]], alt)
}

const cupStackIcon: Draw = (c, color, alt) => {
  poly(c, [[30, 14], [70, 14], [64, 46], [36, 46]], alt)
  poly(c, [[26, 44], [74, 44], [66, 90], [34, 90]], color)
}

const plateStackIcon: Draw = (c, color, alt) => {
  for (const [i, y] of [70, 54, 38].entries()) {
    rect(c, 16 + i * 4, y, 68 - i * 8, 14, 7, i === 2 ? alt : color)
  }
}

const potIcon: Draw = (c, color, alt) => {
  rect(c, 20, 40, 60, 42, 10, color)
  rect(c, 14, 30, 72, 12, 6, alt)
  c.strokeStyle = INK
  c.lineWidth = 6
  c.beginPath()
  c.moveTo(44, 24)
  c.quadraticCurveTo(50, 8, 58, 22)
  c.stroke()
}

const counterIcon: Draw = (c, color, alt) => {
  rect(c, 12, 44, 76, 40, 6, color)
  rect(c, 8, 34, 84, 14, 6, alt)
  circle(c, 68, 26, 10, css('uiCoin'))
}

const binIcon: Draw = (c, color, alt) => {
  poly(c, [[28, 34], [72, 34], [66, 88], [34, 88]], color)
  rect(c, 22, 18, 56, 14, 6, alt)
}

const menuIcon: Draw = (c, color, alt) => {
  rect(c, 14, 16, 72, 58, 8, color)
  for (const [x, y] of [[30, 32], [58, 32], [30, 56], [58, 56]] as [number, number][]) {
    circle(c, x, y, 9, alt)
  }
  rect(c, 44, 74, 12, 16, 3, alt)
}

const caseIcon: Draw = (c, color, alt) => {
  rect(c, 10, 30, 80, 46, 8, alt)
  for (const x of [26, 50, 74]) circle(c, x, 52, 10, color)
  rect(c, 10, 74, 80, 12, 4, color)
}

const sundaeIcon: Draw = (c, color, alt) => {
  rect(c, 38, 62, 24, 26, 5, alt)
  poly(c, [[28, 62], [72, 62], [62, 40], [38, 40]], alt)
  circle(c, 42, 36, 13, color)
  circle(c, 60, 32, 13, color)
  circle(c, 52, 16, 7, css('cherry'))
}

const couchIcon: Draw = (c, color, alt) => {
  rect(c, 12, 40, 76, 20, 8, alt)
  rect(c, 12, 56, 76, 22, 8, color)
  rect(c, 8, 48, 14, 30, 6, alt)
  rect(c, 78, 48, 14, 30, 6, alt)
}

const tableIcon: Draw = (c, color, alt) => {
  rect(c, 10, 36, 80, 16, 8, color)
  rect(c, 22, 52, 12, 34, 4, alt)
  rect(c, 66, 52, 12, 34, 4, alt)
}

const stoolIcon: Draw = (c, color, alt) => {
  circle(c, 50, 36, 24, color)
  rect(c, 44, 52, 12, 34, 4, alt)
  rect(c, 28, 82, 44, 10, 4, alt)
}

const shelfIcon: Draw = (c, color, alt) => {
  rect(c, 16, 14, 68, 74, 6, alt)
  for (const y of [26, 50]) {
    for (const x of [24, 36, 48, 60]) rect(c, x, y, 9, 20, 2, color)
  }
}

const fridgeIcon: Draw = (c, color, alt) => {
  rect(c, 24, 10, 52, 80, 8, color)
  c.strokeStyle = INK
  c.lineWidth = 5
  c.beginPath()
  c.moveTo(24, 40)
  c.lineTo(76, 40)
  c.stroke()
  rect(c, 62, 22, 7, 14, 3, alt)
  rect(c, 62, 50, 7, 14, 3, alt)
}

const cookerIcon: Draw = (c, color, alt) => {
  rect(c, 16, 34, 68, 54, 8, color)
  for (const [x, y] of [[34, 22], [66, 22]] as [number, number][]) circle(c, x, y, 11, alt)
  rect(c, 26, 52, 48, 26, 5, alt)
}

const sinkIcon: Draw = (c, color, alt) => {
  rect(c, 14, 42, 72, 34, 10, color)
  c.strokeStyle = INK
  c.lineWidth = 7
  c.beginPath()
  c.moveTo(50, 40)
  c.lineTo(50, 20)
  c.quadraticCurveTo(50, 12, 68, 14)
  c.stroke()
  circle(c, 50, 60, 8, alt)
}

const bathIcon: Draw = (c, color, alt) => {
  poly(c, [[14, 40], [86, 40], [76, 84], [24, 84]], color)
  for (const [x, y] of [[36, 30], [52, 22], [68, 32]] as [number, number][]) circle(c, x, y, 9, alt)
}

const tellyIcon: Draw = (c, color, alt) => {
  rect(c, 12, 20, 76, 50, 8, alt)
  rect(c, 20, 28, 60, 34, 4, color)
  rect(c, 40, 70, 20, 8, 3, alt)
  rect(c, 28, 78, 44, 10, 4, alt)
}

const benchIcon: Draw = (c, color, alt) => {
  rect(c, 10, 34, 80, 12, 5, color)
  rect(c, 10, 52, 80, 14, 6, color)
  rect(c, 18, 66, 10, 24, 3, alt)
  rect(c, 72, 66, 10, 24, 3, alt)
}

const rugIcon: Draw = (c, color, alt) => {
  circle(c, 50, 52, 36, color)
  circle(c, 50, 52, 20, alt)
}

const posterIcon: Draw = (c, color, alt) => {
  rect(c, 18, 12, 64, 76, 6, alt)
  circle(c, 50, 42, 18, color)
  circle(c, 43, 38, 5, INK)
  circle(c, 57, 38, 5, INK)
}

const clockIcon: Draw = (c, color, alt) => {
  circle(c, 50, 50, 34, color)
  circle(c, 50, 50, 22, alt)
  c.strokeStyle = INK
  c.lineWidth = 6
  c.beginPath()
  c.moveTo(50, 50)
  c.lineTo(50, 34)
  c.moveTo(50, 50)
  c.lineTo(64, 56)
  c.stroke()
}

const fishIcon: Draw = (c, color, alt) => {
  rect(c, 12, 24, 76, 56, 8, alt)
  circle(c, 46, 52, 12, color)
  poly(c, [[58, 52], [74, 42], [74, 62]], color)
}

const paintIcon: Draw = (c, color, alt) => {
  rect(c, 26, 34, 48, 54, 8, alt)
  rect(c, 22, 26, 56, 12, 5, color)
  circle(c, 50, 60, 13, color)
}

const swatchIcon: Draw = (c, color, alt) => {
  rect(c, 12, 30, 40, 40, 5, color)
  rect(c, 48, 46, 40, 40, 5, alt)
}

const toyboxIcon: Draw = (c, color, alt) => {
  rect(c, 16, 42, 68, 46, 8, color)
  rect(c, 12, 30, 76, 16, 6, alt)
  circle(c, 50, 64, 10, alt)
}

/**
 * THE MAILBOX THAT READ AS A HAMMER (fixed r2).
 *
 * It was a wide coloured slab on a short stubby post with a red tab on the
 * side, and on a 116px shop card that is a mallet — the same silhouette as the
 * build/play toggle, which is the single most important button in the game. A
 * kid seeing the shop's most expensive-looking picture as "the hammer again" is
 * the ambiguity the critic named.
 *
 * Now: a TALL thin post with a narrow arch-topped box, an open flap, a letter
 * sticking out of it and the flag up. Nothing about it is a hammer.
 */
const mailboxIcon: Draw = (c, color, alt) => {
  rect(c, 45, 52, 10, 42, 4, alt)
  rect(c, 34, 88, 32, 8, 3, alt)
  // arch-topped box
  c.beginPath()
  c.moveTo(26, 56)
  c.lineTo(26, 34)
  c.arcTo(26, 14, 50, 14, 20)
  c.arcTo(74, 14, 74, 34, 20)
  c.lineTo(74, 56)
  c.closePath()
  fill(c, color)
  // the letter, half out
  poly(c, [[38, 34], [70, 26], [72, 42], [40, 50]], css('uiPanel'))
  // flag up
  rect(c, 78, 20, 6, 30, 2, alt)
  poly(c, [[84, 20], [96, 26], [84, 32]], css('uiDanger'))
}

/**
 * A FLOOR LAMP, not the table lamp. Same family, different silhouette: a tall
 * thin stem on a small disc foot with a cone shade high up. Without this the
 * standing lamp fell through to the generic `light` glyph and was the table
 * lamp's twin on the shelf.
 */
const floorLampIcon: Draw = (c, color, alt) => {
  rect(c, 46, 34, 8, 50, 3, alt)
  poly(c, [[30, 34], [70, 34], [78, 8], [22, 8]], color)
  circle(c, 50, 88, 14, alt)
}

/* -- topping jars, told apart by WHAT COMES OUT, not by hue ----------------- */
/*
 * Five different jars all drew the same jar with a different fill colour, and
 * three of those colours are reds. A pre-reader comparing two red jars has
 * nothing to go on. Each jar now shows its own topping on the lid, so the
 * pictures differ in SHAPE first and colour second.
 */
const jarBody: Draw = (c, color, alt) => {
  rect(c, 30, 38, 40, 50, 10, color)
  rect(c, 26, 22, 48, 18, 6, alt)
}
const jarCherryIcon: Draw = (c, color, alt) => {
  jarBody(c, color, alt)
  c.strokeStyle = INK
  c.lineWidth = 5
  c.lineCap = 'round'
  c.beginPath()
  c.moveTo(50, 22)
  c.quadraticCurveTo(58, 6, 68, 6)
  c.stroke()
  circle(c, 46, 14, 10, css('cherry'))
  circle(c, 66, 12, 8, css('cherry'))
  circle(c, 50, 62, 9, css('uiPanel'))
}
const jarSprinklesIcon: Draw = (c, color, alt) => {
  jarBody(c, color, alt)
  c.strokeStyle = INK
  c.lineWidth = 6
  c.lineCap = 'round'
  for (const [x, y, dx, dy] of [
    [34, 14, 8, -6],
    [50, 8, 9, 5],
    [66, 14, 6, -8],
  ] as [number, number, number, number][]) {
    c.beginPath()
    c.moveTo(x, y)
    c.lineTo(x + dx, y + dy)
    c.stroke()
  }
  for (const [x, y] of [[40, 56], [58, 64], [46, 74]] as [number, number][]) {
    circle(c, x, y, 5, css('uiPanel'))
  }
}
const jarSauceIcon: Draw = (c, color, alt) => {
  jarBody(c, color, alt)
  // a fat drip running off the lid
  c.beginPath()
  c.moveTo(38, 22)
  c.quadraticCurveTo(38, 4, 50, 4)
  c.quadraticCurveTo(62, 4, 62, 22)
  c.closePath()
  fill(c, color)
  circle(c, 50, 64, 11, css('uiPanel'))
}
const jarGratedIcon: Draw = (c, color, alt) => {
  jarBody(c, color, alt)
  for (const [x, y] of [[36, 14], [50, 8], [64, 14]] as [number, number][]) {
    poly(c, [[x - 9, y + 8], [x, y - 8], [x + 9, y + 8]], css('flowerYellow'))
  }
  poly(c, [[40, 70], [50, 54], [60, 70]], css('uiPanel'))
}

const signIcon: Draw = (c, color, alt) => {
  rect(c, 44, 46, 12, 44, 4, alt)
  rect(c, 14, 12, 72, 40, 8, color)
  circle(c, 50, 32, 12, alt)
}

const lampPostIcon: Draw = (c, color, alt) => {
  rect(c, 45, 34, 10, 56, 4, alt)
  rect(c, 30, 84, 40, 10, 4, alt)
  poly(c, [[32, 34], [68, 34], [60, 12], [40, 12]], color)
}

const rockIcon: Draw = (c, color, alt) => {
  poly(c, [[16, 78], [30, 44], [56, 34], [82, 56], [80, 78]], color)
  poly(c, [[30, 44], [56, 34], [52, 56]], alt)
}

const pondIcon: Draw = (c, color, alt) => {
  circle(c, 50, 54, 34, color)
  c.strokeStyle = alt
  c.lineWidth = 6
  for (const y of [44, 62]) {
    c.beginPath()
    c.moveTo(28, y)
    c.quadraticCurveTo(40, y - 8, 50, y)
    c.quadraticCurveTo(60, y + 8, 72, y)
    c.stroke()
  }
}

const bushIcon: Draw = (c, color, alt) => {
  circle(c, 34, 60, 20, color)
  circle(c, 66, 60, 20, color)
  circle(c, 50, 44, 24, alt)
}

const planterIcon: Draw = (c, color, alt) => {
  poly(c, [[16, 54], [84, 54], [76, 88], [24, 88]], alt)
  circle(c, 34, 40, 13, color)
  circle(c, 50, 32, 13, color)
  circle(c, 66, 40, 13, color)
}

const pottedIcon: Draw = (c, color, alt) => {
  poly(c, [[30, 56], [70, 56], [64, 88], [36, 88]], alt)
  circle(c, 50, 38, 20, color)
  circle(c, 34, 44, 12, color)
  circle(c, 66, 44, 12, color)
}

const giftIcon: Draw = (c, color, alt) => {
  rect(c, 18, 36, 64, 52, 6, color)
  rect(c, 14, 24, 72, 16, 5, alt)
  rect(c, 44, 24, 12, 64, 3, alt)
}

const cakeIcon: Draw = (c, color, alt) => {
  rect(c, 22, 44, 56, 30, 6, color)
  rect(c, 14, 74, 72, 12, 5, alt)
  circle(c, 50, 34, 9, css('cherry'))
}

const shopFrontIcon: Draw = (c, color, alt) => {
  rect(c, 12, 34, 76, 54, 6, alt)
  poly(c, [[8, 34], [92, 34], [84, 16], [16, 16]], color)
  rect(c, 38, 52, 24, 36, 4, color)
}

const iceShopIcon: Draw = (c, color, alt) => {
  rect(c, 12, 40, 76, 48, 6, alt)
  poly(c, [[8, 40], [92, 40], [84, 22], [16, 22]], color)
  poly(c, [[40, 56], [60, 56], [50, 84]], css('cone'))
  circle(c, 50, 52, 12, css('iceStrawberry'))
}

const boardIcon: Draw = (c, color, alt) => {
  rect(c, 44, 58, 12, 34, 4, alt)
  rect(c, 10, 8, 80, 52, 6, color)
  rect(c, 20, 18, 26, 14, 3, alt)
  rect(c, 54, 18, 26, 14, 3, alt)
  rect(c, 20, 38, 26, 14, 3, alt)
}

const coinIcon: Draw = (c, color, alt) => {
  for (const x of [16, 34, 66, 84]) poly(c, [[x, 90], [x - 5, 62], [x + 5, 62]], alt)
  circle(c, 50, 48, 30, color)
  circle(c, 50, 48, 18, alt)
}

const lampTableIcon: Draw = (c, color, alt) => {
  poly(c, [[30, 22], [70, 22], [80, 54], [20, 54]], color)
  rect(c, 45, 54, 10, 26, 3, alt)
  rect(c, 30, 78, 40, 10, 4, alt)
}

/**
 * id fragment -> picture. First match wins, so put specific before general.
 * A def with its own `icon2d` still beats everything here.
 */
const PIECE_ICONS: readonly (readonly [string, Draw])[] = [
  ['venue_tub', tubIcon],
  // Toppings, specific before general — each jar shows its own topping so five
  // jars are five pictures rather than five colours of one picture (r2 fix).
  ['venue_top_cherry', jarCherryIcon],
  ['venue_top_sprinkles', jarSprinklesIcon],
  ['venue_top_sauce', jarSauceIcon],
  ['venue_top', jarIcon],
  ['rest_sauce', jarSauceIcon],
  ['rest_cheese', jarGratedIcon],
  // Pieces that used to fall through to a CATEGORY glyph and so wore somebody
  // else's picture on the shelf: the standing lamp was the table lamp's twin,
  // the shop's potted plant was a generic flower, and the shop rug — a rug —
  // was drawn as an ice cream, because `venueFixture` defaults to food.
  ['light_floor_lamp', floorLampIcon],
  ['venue_plant', pottedIcon],
  ['venue_rug', rugIcon],
  ['venue_cones', coneStackIcon],
  ['venue_cups', cupStackIcon],
  ['rest_plates', plateStackIcon],
  ['rest_pot', potIcon],
  ['venue_counter', counterIcon],
  ['venue_bin', binIcon],
  ['venue_menu', menuIcon],
  ['venue_display', caseIcon],
  ['venue_special', sundaeIcon],
  ['venue_table', tableIcon],
  ['venue_stool', stoolIcon],
  ['furn_couch', couchIcon],
  ['furn_table', tableIcon],
  ['furn_bookshelf', shelfIcon],
  ['kitchen_fridge', fridgeIcon],
  ['kitchen_stove', cookerIcon],
  ['kitchen_sink', sinkIcon],
  ['bath_tub', bathIcon],
  ['appliance_tv', tellyIcon],
  ['town_bench', benchIcon],
  ['town_mailbox', mailboxIcon],
  ['town_sign', signIcon],
  ['town_lamp', lampPostIcon],
  ['light_lamp', lampTableIcon],
  ['decor_rug', rugIcon],
  ['decor_poster', posterIcon],
  ['decor_clock', clockIcon],
  ['decor_fish_tank', fishIcon],
  ['decor_cake_stand', cakeIcon],
  ['decor_paint_can', paintIcon],
  ['decor_floor_sample', swatchIcon],
  ['decor_plant', pottedIcon],
  ['toy_box', toyboxIcon],
  ['rock_stone', rockIcon],
  ['water_pond', pondIcon],
  ['plant_bush', bushIcon],
  ['plant_planter', planterIcon],
  ['prop_gift', giftIcon],
  ['venue_shopfront', iceShopIcon],
  ['shop_front', shopFrontIcon],
  ['town_job_board', boardIcon],
  ['town_find_coin', coinIcon],
]

const DRAWINGS: Record<PlaceableCategory, Draw> = {
  block: cube,
  floor: floorIcon,
  wall: wallIcon,
  roof: roofIcon,
  door: doorIcon,
  window: windowIcon,
  stair: stairIcon,
  path: pathIcon,
  fence: fenceIcon,
  furniture: chairIcon,
  bed: bedIcon,
  kitchen: propIcon,
  bath: waterIcon,
  light: lampIcon,
  decor: starIcon,
  plant: plantIcon,
  tree: treeIcon,
  water: waterIcon,
  townProp: propIcon,
  venueFixture: foodIcon,
  toy: starIcon,
  appliance: propIcon,
}

function shade(name: PaletteName): string {
  return css(name)
}

/** Draw the icon for one placeable, filling a `size` x `size` canvas. */
export function drawPieceIcon(c: CanvasRenderingContext2D, size: number, def: AnyPlaceableDef): void {
  c.clearRect(0, 0, size, size)
  c.save()
  c.scale(size / 100, size / 100)
  if (def.icon2d !== undefined) {
    def.icon2d(c, 100)
    c.restore()
    return
  }
  const main = def.tints?.[0] ?? DEFAULT_COLOR[def.category]
  const alt = def.tints?.[1] ?? DEFAULT_COLOR[def.category]
  const own = PIECE_ICONS.find(([frag]) => def.id.includes(frag))
  const draw = own?.[1] ?? DRAWINGS[def.category] ?? starIcon
  draw(c, shade(main), shade(alt === main ? 'inkSoft' : alt))
  c.restore()
}

/** Draw a category tab's icon (same vocabulary, so tabs match their contents). */
export function drawCategoryIcon(c: CanvasRenderingContext2D, size: number, cat: PlaceableCategory): void {
  c.clearRect(0, 0, size, size)
  c.save()
  c.scale(size / 100, size / 100)
  const draw = DRAWINGS[cat] ?? starIcon
  draw(c, css(DEFAULT_COLOR[cat]), css('inkSoft'))
  c.restore()
}

/** Tool glyphs (rotate / trash / collapse / page). Pictures, never words. */
export function drawToolIcon(
  c: CanvasRenderingContext2D,
  size: number,
  tool: 'rotate' | 'trash' | 'collapse' | 'expand' | 'pagePrev' | 'pageNext' | 'pageUp' | 'pageDown',
): void {
  c.clearRect(0, 0, size, size)
  c.save()
  c.scale(size / 100, size / 100)
  c.lineWidth = 9
  c.lineCap = 'round'
  c.lineJoin = 'round'
  c.strokeStyle = INK
  if (tool === 'pageUp' || tool === 'pageDown') {
    // "More pieces this way", stacked beside the tile block. Vertical because
    // two 88px arrows SIDE BY SIDE left a 155px strip on a portrait iPad — one
    // tile wide, seven pages deep. A column costs 88px instead of 196.
    const up = tool === 'pageUp'
    const tip = up ? 26 : 74
    const back = up ? 58 : 42
    c.beginPath()
    c.moveTo(20, back)
    c.lineTo(50, tip)
    c.lineTo(80, back)
    c.stroke()
    for (const i of [0, 1, 2]) circle(c, 30 + i * 20, up ? 78 : 22, 5, css('uiAccent'))
  } else if (tool === 'pagePrev' || tool === 'pageNext') {
    // "More pieces this way." A fat chevron with three dots behind it, so it
    // reads as a page turn rather than as another piece to place.
    const left = tool === 'pagePrev'
    const tip = left ? 30 : 70
    const back = left ? 62 : 38
    c.beginPath()
    c.moveTo(back, 20)
    c.lineTo(tip, 50)
    c.lineTo(back, 80)
    c.stroke()
    for (const i of [0, 1, 2]) circle(c, left ? 76 : 24, 30 + i * 20, 5, css('uiAccent'))
  } else if (tool === 'rotate') {
    c.beginPath()
    c.arc(50, 52, 28, Math.PI * 0.75, Math.PI * 2.35)
    c.stroke()
    poly(c, [[70, 6], [88, 30], [58, 32]], css('uiAccent'))
  } else if (tool === 'trash') {
    rect(c, 30, 16, 40, 12, 4, css('uiDanger'))
    poly(c, [[26, 32], [74, 32], [68, 88], [32, 88]], css('uiDanger'))
    c.strokeStyle = INK
    c.lineWidth = 6
    c.beginPath()
    c.moveTo(42, 44)
    c.lineTo(44, 76)
    c.moveTo(58, 44)
    c.lineTo(56, 76)
    c.stroke()
  } else {
    const up = tool === 'expand'
    c.beginPath()
    c.moveTo(24, up ? 62 : 38)
    c.lineTo(50, up ? 34 : 66)
    c.lineTo(76, up ? 62 : 38)
    c.stroke()
  }
  c.restore()
}
